import { useEffect, useState, ChangeEvent } from 'react';
import { Heading } from 'react-aria-components';
import toast, { Toaster } from 'react-hot-toast';
import { useParams, Link } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getLineSupportRecordByReservationID, patchSupportRecord } from '@/api/getSupportRecord';
import Button from '@/components/Button/Button';
import { LineUserAtom } from '@/jotai/jotai';
import { SupportRecord } from '@/types/supportRecord';

const UserContactDetailPage = () => {
  // 新しいURLパターンに合わせてパラメータを取得
  const { childId, reservationId } = useParams<{ childId: string; reservationId: string }>();
  const [lineUser] = useAtom(LineUserAtom);

  const [contactData, setContactData] = useState<SupportRecord | null>(null);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValid = userInput.trim().length > 0;

  useEffect(() => {
    const fetchContactDetail = async () => {
      try {
        setIsLoading(true);

        // 予約IDの検証
        const reservation = reservationId ? parseInt(reservationId, 10) : 0;
        if (!reservation) {
          setError('無効なIDです');
          setIsLoading(false);
          return;
        }

        // APIからデータを取得
        const response = await getLineSupportRecordByReservationID(
          reservation,
          lineUser?.accessToken ?? ''
        );

        if (response.status === 200 && response.result) {
          console.log(response.result);
          setContactData(response.result);

          //   // 保護者からの連絡事項があれば初期表示
          //   if (response.result.guardian_message) {
          //     setUserInput(response.result.guardian_message);
          //   }
        } else {
          setError('データの取得に失敗しました');
        }
      } catch (err) {
        console.error('Error fetching contact detail:', err);
        setError('データの取得中にエラーが発生しました');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContactDetail();
  }, [reservationId]);

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setUserInput(e.target.value);
  };

  const formatDateTime = () => {
    const now = new Date();
    const dateString = now.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const timeString = now.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    return `\n(${dateString} ${timeString} 更新)\n\n`;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (!contactData) return;

      // 更新時間を付与する
      const updatedUserInput = `${userInput} ${formatDateTime()}`;

      const result = await patchSupportRecord(
        lineUser?.lineID ?? '',
        lineUser?.accessToken ?? '',
        contactData.reservation_id,
        updatedUserInput
      );

      if (result.status === 200) {
        // 既存のメッセージに新しいメッセージを追加
        setContactData((prevData) => {
          if (!prevData) return null;

          // 既存のメッセージがある場合は改行を追加
          const updatedMessage = prevData.guardian_message
            ? `${prevData.guardian_message}${updatedUserInput}`
            : updatedUserInput;

          return {
            ...prevData,
            guardian_message: updatedMessage,
          };
        });
        // 入力フィールドをクリア
        setUserInput('');

        toast.success('連絡事項を送信しました', {
          duration: 5000, // 5秒間表示
        });
      } else {
        toast.success('送信に失敗しました', {
          duration: 5000, // 5秒間表示
        });
        return;
      }

      // 少し待ってから画面遷移
      //   setTimeout(() => {
      //     if (childId) {
      //       navigate(`/user/contact/children/${childId}/history`);
      //     }
      //   }, 1500);
    } catch (err) {
      console.error('Error submitting message:', err);
      toast.error('送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 日付を「YYYY年MM月DD日」の形式に変換する関数
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();

      return `${year}年${month}月${day}日`;
    } catch (err) {
      console.error('Error formatting date:', err);
      return '';
    }
  };

  // ローディング中の表示
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="text-gray-500">データを読み込み中...</div>
        </div>
      </div>
    );
  }

  // エラー時の表示
  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-white p-4">
        <div className="mb-6 text-center">
          <div className="text-lg font-medium text-red-500">{error}</div>
        </div>
        <Link
          to="/user/contact/children"
          className="text-blue-700 underline"
        >
          お子様選択へ戻る
        </Link>
      </div>
    );
  }

  // データがない場合
  if (!contactData) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-white p-4">
        <div className="mb-6 text-center">
          <div className="text-lg font-medium text-gray-500">連絡帳が見つかりません</div>
        </div>
        <Link
          to="/user/contact/children"
          className="text-blue-700 underline"
        >
          お子様選択へ戻る
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            fontSize: '24px',
            padding: '20px',
          },
        }}
      />
      {/* ヘッダー */}
      <div className="px-4 py-3 text-xl font-bold">連絡帳</div>

      {/* 利用者情報 */}
      <div className="mx-4 mb-4 rounded-lg bg-blue-100 p-3">
        <Heading className="text-base font-medium">
          {`${contactData.child.last_name} ${contactData.child.first_name}`}
        </Heading>
      </div>

      {/* 本文コンテンツ */}
      <div className="px-4">
        <div className="mb-4 text-base font-medium">{formatDate(contactData.date)}の様子</div>

        <div className="space-y-4">
          {/* 担当スタッフ */}
          <section>
            <div className="mb-2 text-base font-bold">担当スタッフ</div>
            <div className="bg-white p-2">
              <div className="text-base">{contactData.recorder?.name || '記録なし'}</div>
            </div>
            <div className="mt-3 h-px bg-gray-200" />
          </section>

          {/* 体温 */}
          <section>
            <div className="mb-2 text-base font-bold">来所時の体温</div>
            <div className="bg-white p-2">
              <div className="text-base">
                {contactData.body_temperature ? `${contactData.body_temperature}℃` : '記録なし'}
              </div>
            </div>
            <div className="mt-3 h-px bg-gray-200" />
          </section>

          {/* 体調 */}
          {contactData.condition && (
            <section>
              <div className="mb-2 text-base font-bold">体調</div>
              <div className="bg-white p-2">
                <div className="text-base">{contactData.condition}</div>
              </div>
              <div className="mt-3 h-px bg-gray-200" />
            </section>
          )}

          {/* 写真 */}
          <section>
            <div className="mb-2 text-base font-bold">写真</div>
            <div className="grid grid-cols-2 gap-2">
              {contactData.support_record_images && contactData.support_record_images.length > 0 ? (
                contactData.support_record_images.map((image, index) => (
                  <div
                    key={index}
                    className="flex aspect-[4/3] items-center justify-center bg-gray-200 text-base text-gray-500"
                  >
                    {/* {`画像 ${index + 1}`} */}
                    <img
                      src={image}
                      alt={`画像 ${index + 1}`}
                    />
                  </div>
                ))
              ) : (
                <div className="col-span-2 py-8 text-center text-gray-400">写真がありません</div>
              )}
            </div>
          </section>

          {/* スタッフからのメッセージ */}
          <section>
            <div className="mb-2 text-base font-bold">保護者への連絡事項</div>
            <div className="bg-white p-2">
              <div className="text-base">
                {contactData.staff_message || contactData.staff_report || '連絡事項はありません'}
              </div>
            </div>
            <div className="mt-3 h-px bg-gray-200" />
          </section>

          {/* 支援内容 */}
          {contactData.support_content && (
            <section>
              <div className="mb-2 text-base font-bold">支援内容</div>
              <div className="bg-white p-2">
                <div className="text-base">{contactData.support_content}</div>
              </div>
              <div className="mt-3 h-px bg-gray-200" />
            </section>
          )}

          {/* 保護者からの連絡事項 */}
          <section>
            <div className="mb-2 text-base font-bold">保護者からの連絡事項</div>
            {contactData.guardian_message && (
              <div className="mb-3 bg-white p-2">
                <div className="whitespace-pre-wrap text-base">{contactData.guardian_message}</div>
              </div>
            )}
            <div className="rounded-lg border">
              <textarea
                className="min-h-[80px] w-full resize-none rounded-lg p-2 text-base outline-none"
                placeholder="連絡事項を入力してください"
                value={userInput}
                onChange={handleInputChange}
              />
            </div>
          </section>
        </div>
      </div>

      {/* 固定フッター */}
      <div className="fixed bottom-0 left-0 right-0 space-y-2 bg-white p-4">
        <Button
          className="w-full rounded bg-blue-700 py-3 text-base text-white"
          variant="solid-fill"
          size="lg"
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? '送信中...' : '送信'}
        </Button>
        {childId && (
          <Link
            className="block w-full py-2 text-center text-base text-blue-700 underline"
            to={`/user/contact/children/${childId}/history`}
          >
            戻る
          </Link>
        )}
      </div>
    </div>
  );
};

export default UserContactDetailPage;
